import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Button,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Spinner,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import classnames from "classnames";
import { ExamenService } from "service/ExamenService";
import { CicloService } from "service/CicloService";
import FSelect from "components/Select/FSelect";
import FDateInput from "components/DateInput/FDateInput";

const TIPO_EXAMEN_MODULO = "5eed7526dd4650b195532696";

const minToTime = (min) => {
  if (min == null || min === "") return "";
  const h = Math.floor(min / 60).toString().padStart(2, "0");
  const m = (min % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
};

const timeToMin = (str) => {
  if (!str) return 0;
  const [h, m] = str.split(":").map(Number);
  return h * 60 + m;
};

function Examen() {
  const notificationAlertRef = useRef(null);

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [activeTab, setActiveTab] = useState("1");
  const [listExamenesVirtuales, setListExamenesVirtuales] = useState([]);
  const [listExamenesPresenciales, setListExamenesPresenciales] = useState([]);
  const [listCiclos, setListCiclos] = useState([]);
  const [listSemanas, setListSemanas] = useState([]);
  const [selectedCiclo, setSelectedCiclo] = useState(localStorage.getItem("_c") || "");

  // Estado para modales
  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [modalRankingOpen, setModalRankingOpen] = useState(false);
  const [modalNotasOpen, setModalNotasOpen] = useState(false);
  const [modalSubirNotasOpen, setModalSubirNotasOpen] = useState(false);
  const [selectedExamen, setSelectedExamen] = useState(null);
  const [tipoEdicion, setTipoEdicion] = useState("virtual");
  const [formExamen, setFormExamen] = useState({});
  const [listRanking, setListRanking] = useState([]);
  const [listNotas, setListNotas] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [selectedSemanaId, setSelectedSemanaId] = useState("");
  const [modalRankingSemanaOpen, setModalRankingSemanaOpen] = useState(false);
  const [listRankingSemana, setListRankingSemana] = useState([]);
  const [loadingRankingSemana, setLoadingRankingSemana] = useState(false);

  // Modal de confirmación (reemplaza window.confirm)
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  const examenService = new ExamenService();
  const cicloService = new CicloService();

  useEffect(() => {
    loadCiclos();
    loadSemanas();
  }, []);

  useEffect(() => {
    if (selectedCiclo) {
      loadExamenes();
    }
  }, [selectedCiclo, activeTab]);

  const loadSemanas = async () => {
    try {
      const res = await examenService.getSemanas();
      const semanas = (res?.data || []).filter(s => s.iditem !== 0);
      setListSemanas(semanas);
    } catch (error) {
      console.error("Error cargando semanas:", error);
    }
  };

  const loadCiclos = async () => {
    try {
      const res = await cicloService.getCiclos();
      const ciclos = res?.data || [];
      setListCiclos(ciclos);
      if (!selectedCiclo && ciclos.length > 0) {
        setSelectedCiclo(ciclos[0]._id);
      }
    } catch (error) {
      console.error("Error cargando ciclos:", error);
    }
  };

  const loadExamenes = async () => {
    try {
      if (activeTab === "1") {
        const res = await examenService.getExamenesVirtuales();
        const data = (res?.data || []).filter(e => (e.ciclo?._id || e.ciclo) === selectedCiclo);
        setListExamenesVirtuales(data);
      } else {
        const res = await examenService.getExamenesPresenciales();
        const data = (res?.data || []).filter(e => (e.ciclo?._id || e.ciclo) === selectedCiclo);
        setListExamenesPresenciales(data);
      }
    } catch (error) {
      console.error("Error cargando exámenes:", error);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleNuevoExamen = () => {
    const tipo = activeTab === "1" ? "virtual" : "presencial";
    setTipoEdicion(tipo);
    setSelectedExamen(null);
    setFormExamen({
      ciclo: selectedCiclo,
      estado: "Borrador",
      tipo_examen: TIPO_EXAMEN_MODULO,
      tiempomin: 90,
      horainicio: "08:00",
      horafin: "10:00",
    });
    setIsCreating(true);
    setModalEditOpen(true);
  };

  const handleEditVirtual = (examen) => {
    setTipoEdicion("virtual");
    setSelectedExamen(examen);
    setFormExamen({ ...examen });
    setIsCreating(false);
    setModalEditOpen(true);
  };

  const handleEditPresencial = (examen) => {
    setTipoEdicion("presencial");
    setSelectedExamen(examen);
    setFormExamen({
      ...examen,
      ciclo: examen.ciclo?._id || examen.ciclo,
      semana: examen.semana?._id || examen.semana,
      horainicio: minToTime(examen.horainiciomin),
      horafin: minToTime(examen.horafinmin),
      fecha: examen.fecha ? examen.fecha.substring(0, 10) : "",
    });
    setIsCreating(false);
    setModalEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setLoadingSave(true);
    try {
      let payload = { ...formExamen };
      if (tipoEdicion === "presencial") {
        payload.horainiciomin = timeToMin(formExamen.horainicio);
        payload.horafinmin = timeToMin(formExamen.horafin);
        delete payload.horainicio;
        delete payload.horafin;
      }
      if (isCreating) {
        if (tipoEdicion === "virtual") {
          await examenService.addExamenVirtual(payload);
        } else {
          await examenService.addExamenPresencial(payload);
        }
        notify("success", "Examen creado correctamente.");
      } else {
        if (tipoEdicion === "virtual") {
          await examenService.updateExamenVirtual(payload);
        } else {
          await examenService.updateExamenPresencial(payload);
        }
        notify("success", "Examen actualizado correctamente.");
      }
      setModalEditOpen(false);
      loadExamenes();
    } catch (error) {
      console.error("Error guardando examen:", error);
      notify("danger", "Error al guardar el examen. Intenta nuevamente.");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleRanking = async (examen) => {
    setSelectedExamen(examen);
    setListRanking([]);
    setModalRankingOpen(true);
    setLoadingModal(true);
    try {
      const res = await examenService.getRankingVirtual(examen._id);
      setListRanking(res?.data || []);
    } catch (error) {
      console.error("Error cargando ranking:", error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleNotas = async (examen) => {
    setSelectedExamen(examen);
    setListNotas([]);
    setModalNotasOpen(true);
    setLoadingModal(true);
    try {
      const res = await examenService.getNotasPresencial(examen._id);
      const sorted = (res?.data || []).slice().sort((a, b) => (b.nota || 0) - (a.nota || 0));
      setListNotas(sorted);
    } catch (error) {
      console.error("Error cargando notas:", error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleSubirNotas = (examen) => {
    setSelectedExamen(examen);
    setUploadFile(null);
    setModalSubirNotasOpen(true);
  };

  const handleUploadNotas = async () => {
    if (!uploadFile) {
      notify("warning", "Selecciona un archivo Excel (.xlsx).");
      return;
    }
    setLoadingUpload(true);
    try {
      const idCiclo = selectedExamen.ciclo?._id || selectedExamen.ciclo;
      const formData = new FormData();
      formData.append("file", uploadFile);
      const res = await examenService.uploadNotasExcel(selectedExamen._id, idCiclo, formData);
      const data = res?.data;
      if (data === null) {
        notify("warning", "No se importaron notas: no hay alumnos matriculados en el ciclo de este examen.");
      } else {
        const cantidad = Array.isArray(data) ? data.length : 0;
        if (cantidad === 0) {
          notify("warning", "No se importaron notas: los DNIs del Excel no coinciden con alumnos matriculados en este ciclo.");
        } else {
          notify("success", `Se registraron ${cantidad} nota(s) correctamente.`);
          setModalSubirNotasOpen(false);
        }
      }
    } catch (error) {
      console.error("Error subiendo notas:", error);
      notify("danger", "Error al subir el archivo. Verifica el formato.");
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleEliminar = (examen) => {
    setConfirmData({
      mensaje: `¿Estás seguro de eliminar el examen "${examen.descripcion}"? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        setConfirmModal(false);
        examenService.deleteExamenPresencial(examen._id)
          .then(() => {
            loadExamenes();
            notify("success", "Examen eliminado correctamente.");
          })
          .catch(() => notify("danger", "Error al eliminar el examen."));
      },
    });
    setConfirmModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormExamen(prev => ({ ...prev, [name]: value }));
  };

  const handleRankingSemana = async () => {
    if (!selectedSemanaId) return;
    setListRankingSemana([]);
    setModalRankingSemanaOpen(true);
    setLoadingRankingSemana(true);
    try {
      const res = await examenService.getRankingFecha(selectedSemanaId);
      setListRankingSemana(res?.data || []);
    } catch (err) {
      console.error("Error cargando ranking semana:", err);
    } finally {
      setLoadingRankingSemana(false);
    }
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Gestión de Exámenes Académicos</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="11">
                    <FormGroup className="mb-0">
                      <Label for="selectedCiclo">Ciclo Académico</Label>
                      <FSelect
                        id="selectedCiclo"
                        value={selectedCiclo}
                        onChange={(e) => {
                          setSelectedCiclo(e.target.value);
                          localStorage.setItem("_c", e.target.value);
                        }}
                        options={listCiclos.map(c => ({ value: c._id, label: c.denominacion }))}
                        placeholder="Seleccione Ciclo"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={loadExamenes}
                        style={{ height: "40px", padding: "0" }}
                        title="Refrescar"
                      >
                        <i className="tim-icons icon-refresh-01" />
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones y Navegación */}
                <Row className="mb-4 align-items-center">
                  <Col md="8">
                    <Nav tabs className="border-0">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "1" }, "px-4")}
                          onClick={() => toggleTab("1")}
                          style={{ cursor: 'pointer', border: 'none' }}
                        >
                          <i className="tim-icons icon-laptop mr-2" />
                          Virtuales
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "2" }, "px-4")}
                          onClick={() => toggleTab("2")}
                          style={{ cursor: 'pointer', border: 'none' }}
                        >
                          <i className="tim-icons icon-paper mr-2" />
                          Presenciales
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="primary" className="btn-fill" onClick={handleNuevoExamen} disabled={activeTab === "1"}>
                      <i className="tim-icons icon-simple-add mr-2" />
                      Nuevo Examen
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />

                <TabContent activeTab={activeTab}>


                  {/* Tab Virtuales */}
                  <TabPane tabId="1">
                    <Row className="align-items-end mb-3">
                      <Col md="5">
                        <FormGroup className="mb-0">
                          <Label>Ranking Semanal</Label>
                          <FSelect
                            value={selectedSemanaId}
                            onChange={(e) => setSelectedSemanaId(e.target.value)}
                            options={listSemanas.map(s => ({ value: s._id, label: s.denominacion }))}
                            placeholder="Seleccione semana"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <Button
                          color="info"
                          className="btn-fill"
                          onClick={handleRankingSemana}
                          disabled={!selectedSemanaId}
                          style={{ marginTop: "24px" }}
                        >
                          <i className="tim-icons icon-trophy mr-1" /> Ver Ranking
                        </Button>
                      </Col>
                    </Row>
                    <Table>
                      <thead className="text-primary">
                        <tr>
                          <th>Fecha</th>
                          <th>Título</th>
                          <th>Duración</th>
                          <th>Preguntas</th>
                          <th>Estado</th>
                          <th className="text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listExamenesVirtuales.map((e, i) => (
                          <tr key={i}>
                            <td>{fmtDate(e.fecha)}</td>
                            <td>{e.descripcion}</td>
                            <td>{e.duracionmin} min</td>
                            <td>{e.numeropreguntas}</td>
                            <td>{e.estado}</td>
                            <td className="text-right">
                              <Button color="info" size="sm" className="btn-icon btn-link" title="Ranking"
                                onClick={() => handleRanking(e)}>
                                <i className="tim-icons icon-trophy" />
                              </Button>
                              <Button color="warning" size="sm" className="btn-icon btn-link" title="Editar"
                                onClick={() => handleEditVirtual(e)}>
                                <i className="tim-icons icon-pencil" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {listExamenesVirtuales.length === 0 && (
                          <tr><td colSpan="6" className="text-center">No hay exámenes virtuales registrados</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </TabPane>

                  {/* Tab Presenciales */}
                  <TabPane tabId="2">
                    <Table>
                      <thead className="text-primary">
                        <tr>
                          <th>Fecha</th>
                          <th>Título</th>
                          <th>Semana</th>
                          <th>Estado</th>
                          <th className="text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listExamenesPresenciales.map((e, i) => (
                          <tr key={i}>
                            <td>{fmtDate(e.fecha)}</td>
                            <td>{e.descripcion}</td>
                            <td>{e.semana?.denominacion || ""}</td>
                            <td>{e.estado}</td>
                            <td className="text-right">
                              <Button color="success" size="sm" className="btn-icon btn-link" title="Ver Notas"
                                onClick={() => handleNotas(e)}>
                                <i className="tim-icons icon-paper" />
                              </Button>
                              <Button color="info" size="sm" className="btn-icon btn-link" title="Subir Notas (Excel)"
                                onClick={() => handleSubirNotas(e)}>
                                <i className="tim-icons icon-upload" />
                              </Button>
                              <Button color="warning" size="sm" className="btn-icon btn-link" title="Editar"
                                onClick={() => handleEditPresencial(e)}>
                                <i className="tim-icons icon-pencil" />
                              </Button>
                              <Button color="danger" size="sm" className="btn-icon btn-link" title="Eliminar"
                                onClick={() => handleEliminar(e)}>
                                <i className="tim-icons icon-simple-remove" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {listExamenesPresenciales.length === 0 && (
                          <tr><td colSpan="5" className="text-center">No hay exámenes presenciales registrados</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </TabPane>

                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal Editar */}
      <Modal isOpen={modalEditOpen} toggle={() => setModalEditOpen(false)} size="md" backdrop="static" className="modal-black">
        <ModalHeader toggle={() => setModalEditOpen(false)}>
          {isCreating ? "Nuevo" : "Editar"} Examen {tipoEdicion === "virtual" ? "Virtual" : "Presencial"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Título</Label>
                  <Input name="descripcion" value={formExamen.descripcion || ""} onChange={handleFormChange} />
                </FormGroup>
              </Col>
            </Row>
            {tipoEdicion === "virtual" && (
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Duración (min)</Label>
                    <Input type="number" name="duracionmin" value={formExamen.duracionmin || ""} onChange={handleFormChange} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>N° Preguntas</Label>
                    <Input type="number" name="numeropreguntas" value={formExamen.numeropreguntas || ""} onChange={handleFormChange} />
                  </FormGroup>
                </Col>
              </Row>
            )}
            {tipoEdicion === "presencial" && (
              <>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Semana</Label>
                      <FSelect
                        name="semana"
                        value={formExamen.semana || ""}
                        onChange={handleFormChange}
                        options={listSemanas.map(s => ({ value: s._id, label: s.denominacion }))}
                        placeholder="Seleccione semana"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Fecha del examen</Label>
                      <FDateInput name="fecha" value={formExamen.fecha || ""} onChange={handleFormChange} />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>Duración (min)</Label>
                      <Input type="number" name="tiempomin" value={formExamen.tiempomin || ""} onChange={handleFormChange} />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Hora inicio</Label>
                      <Input type="time" name="horainicio" value={formExamen.horainicio || ""} onChange={handleFormChange} />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Hora fin</Label>
                      <Input type="time" name="horafin" value={formExamen.horafin || ""} onChange={handleFormChange} />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Estado</Label>
                  <FSelect
                    name="estado"
                    value={formExamen.estado || ""}
                    onChange={handleFormChange}
                    options={[
                      { value: "Publicado", label: "Publicado" },
                      { value: "Borrador", label: "Borrador" },
                      { value: "Oculto", label: "Oculto" },
                    ]}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalEditOpen(false)} disabled={loadingSave}>Cancelar</Button>
          <Button color="primary" onClick={handleSaveEdit} disabled={loadingSave}>
            {loadingSave ? <><Spinner size="sm" className="mr-1" />Guardando...</> : "Guardar"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Ranking */}
      <Modal isOpen={modalRankingOpen} toggle={() => setModalRankingOpen(false)} size="lg" className="modal-black">
        <ModalHeader toggle={() => setModalRankingOpen(false)}>
          Ranking — {selectedExamen?.descripcion}
        </ModalHeader>
        <ModalBody>
          {loadingModal ? (
            <p className="text-center">Cargando...</p>
          ) : (
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th>#</th>
                  <th>Alumno</th>
                  <th>DNI</th>
                  <th>Nota</th>
                  <th>Correctas</th>
                  <th>Incorrectas</th>
                </tr>
              </thead>
              <tbody>
                {listRanking.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{r.alumno?.nombres} {r.alumno?.apellidopaterno}</td>
                    <td>{r.alumno?.documentoidentidad}</td>
                    <td>{r.nota}</td>
                    <td>{r.cantidadcorrectas}</td>
                    <td>{r.cantidadincorrectas}</td>
                  </tr>
                ))}
                {listRanking.length === 0 && (
                  <tr><td colSpan="6" className="text-center">Sin resultados</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalRankingOpen(false)}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Notas */}
      <Modal isOpen={modalNotasOpen} toggle={() => setModalNotasOpen(false)} size="xl" className="modal-black">
        <ModalHeader toggle={() => setModalNotasOpen(false)}>
          Notas — {selectedExamen?.descripcion}
        </ModalHeader>
        <ModalBody>
          {loadingModal ? (
            <p className="text-center">Cargando...</p>
          ) : (
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th>#</th>
                  <th>DNI</th>
                  <th>Sede</th>
                  <th>Preguntas</th>
                  <th>Correctas</th>
                  <th>Incorrectas</th>
                  <th>Omitidas</th>
                  <th>Nota</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {listNotas.map((n, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{n.documentoidentidad}</td>
                    <td>{n.sede?.denominacion || ""}</td>
                    <td>{n.cantidadpreguntas}</td>
                    <td>{n.cantidadcorrectas}</td>
                    <td>{n.cantidadincorrectas}</td>
                    <td>{n.cantidadomitidas}</td>
                    <td><strong>{n.nota ?? 0}</strong></td>
                    <td>{fmtDate(n.fechacreacion)}</td>
                  </tr>
                ))}
                {listNotas.length === 0 && (
                  <tr><td colSpan="9" className="text-center">Sin notas registradas</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalNotasOpen(false)}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Subir Notas (Excel) */}
      <Modal isOpen={modalSubirNotasOpen} toggle={() => setModalSubirNotasOpen(false)} size="sm" backdrop="static" className="modal-black">
        <ModalHeader toggle={() => setModalSubirNotasOpen(false)}>
          Cargar Notas — {selectedExamen?.descripcion}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="text-muted">Descarga la plantilla, llénala y súbela aquí.</small>
            <a
              href="/files/plantilla_examen_presencial.xlsx"
              download="plantilla_examen_presencial.xlsx"
              className="btn btn-sm btn-outline-info"
            >
              <i className="tim-icons icon-cloud-download-93 mr-1" />
              Plantilla
            </a>
          </div>
          <FormGroup>
            <Label>Archivo Excel (.xlsx)</Label>
            <Input
              type="file"
              accept=".xlsx"
              onChange={(e) => setUploadFile(e.target.files[0] || null)}
            />
          </FormGroup>
          <p className="text-muted" style={{ fontSize: "0.8rem" }}>
            Columnas: DNI · Nota · Cant. Preguntas · Correctas · Incorrectas · Omitidas · ID Sede
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalSubirNotasOpen(false)} disabled={loadingUpload}>Cancelar</Button>
          <Button color="primary" onClick={handleUploadNotas} disabled={loadingUpload}>
            {loadingUpload ? <><Spinner size="sm" className="mr-1" />Subiendo...</> : "Subir"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Ranking Semanal */}
      <Modal isOpen={modalRankingSemanaOpen} toggle={() => setModalRankingSemanaOpen(false)} size="lg" className="modal-black">
        <ModalHeader toggle={() => setModalRankingSemanaOpen(false)}>
          Ranking Semanal — {listSemanas.find(s => s._id === selectedSemanaId)?.denominacion}
        </ModalHeader>
        <ModalBody>
          {loadingRankingSemana ? (
            <p className="text-center">Cargando...</p>
          ) : (
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  <th>#</th>
                  <th>Alumno</th>
                  <th>DNI</th>
                  <th>Nota</th>
                  <th>Correctas</th>
                  <th>Incorrectas</th>
                </tr>
              </thead>
              <tbody>
                {listRankingSemana.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{r.alumno?.nombres} {r.alumno?.apellidopaterno}</td>
                    <td>{r.alumno?.documentoidentidad}</td>
                    <td>{r.nota}</td>
                    <td>{r.cantidadcorrectas}</td>
                    <td>{r.cantidadincorrectas}</td>
                  </tr>
                ))}
                {listRankingSemana.length === 0 && (
                  <tr><td colSpan="6" className="text-center">Sin resultados</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalRankingSemanaOpen(false)}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Confirmación */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered className="modal-black">
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody>
          <p>{confirmData.mensaje}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Examen;
